<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import BrandLogo from './components/BrandLogo.vue'
import AppIcon from './components/AppIcon.vue'
import { useUserStore } from './stores/userStore'
import { useMessagingStore } from './stores/messagingStore'
import { useThemeStore } from './stores/themeStore'
import { roleIsOneOf, normalizeRole } from './utils/roles'
import { useContinuityStore } from './stores/continuityStore'

const route = useRoute()
const userStore = useUserStore()
const messagingStore = useMessagingStore()
const themeStore = useThemeStore()
const continuityStore = useContinuityStore()

const drawerOpen = ref(false)
const isDarkMode = computed(() => themeStore.isDarkMode)

const showChrome = computed(() => {
  // 'Home' ('/') is a pure redirect (landing page removed per owner feedback);
  // Register shares the same chrome-less auth treatment as Signup.
  return !['Home', 'Login', 'Signup', 'Register', 'AuthCallback'].includes(route.name)
})

const isAuthed = computed(() => userStore.isAuthenticated)
// Owner feedback (funnel slice A4): the numbered top navigation is gone.
// Authenticated users get a persistent left sidebar (profile at the top,
// logout pinned to the bottom); on small screens the same sidebar slides
// in as a drawer. Logged-out visitors on public-preview pages keep a slim
// top bar with login/signup so guest previews stay reachable.
const showSidebar = computed(() => showChrome.value && isAuthed.value)

const unreadCount = computed(() => 0) // Placeholder

function toggleTheme() {
  themeStore.toggle()
}

const displayName = computed(() => {
  const u = userStore.user
  if (!u) return ''
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return name || (u.email ? u.email.split('@')[0] : 'My account')
})
const avatarInitial = computed(() => {
  const source = displayName.value || userStore.user?.email || '?'
  return source.charAt(0).toUpperCase()
})
const roleLabel = computed(() => {
  const role = normalizeRole(userStore.user?.role)
  return role.charAt(0).toUpperCase() + role.slice(1)
})

const canOpenDealerPortal = computed(() => roleIsOneOf(userStore.user?.role, ['dealer', 'admin', 'superadmin']))
const canOpenQuotes = computed(() => roleIsOneOf(userStore.user?.role, ['dealer', 'supplier', 'admin', 'superadmin']))
const canOpenOperationsPortal = computed(() => roleIsOneOf(userStore.user?.role, ['operations', 'admin', 'superadmin']))
const canOpenAdminPortal = computed(() => roleIsOneOf(userStore.user?.role, ['admin', 'superadmin']))
const canOpenBreakGlassPortal = computed(() => normalizeRole(userStore.user?.role) === 'superadmin')
const hasRoleLinks = computed(() => canOpenQuotes.value || canOpenDealerPortal.value || canOpenOperationsPortal.value || canOpenAdminPortal.value || canOpenBreakGlassPortal.value)

function closeDrawer() {
  drawerOpen.value = false
}

function openMessages() {
  messagingStore.toggleWidget()
  closeDrawer()
}

watch(() => route.fullPath, closeDrawer)

async function handleLogout() {
  await userStore.logout()
  window.location.href = '/'
}

onMounted(() => {
  themeStore.init()
})
</script>

<template>
  <div id="app" class="app-wrapper">
    <!-- Omnichannel Continuity Banner -->
    <Transition name="slide-down">
      <div v-if="continuityStore.showResumePrompt" class="continuity-banner">
        <div class="banner-content">
          <span class="banner-icon"><AppIcon name="refresh-cw" :size="20" /></span>
          <span class="banner-text">Pick up where you left off on your other device?</span>
          <div class="banner-actions">
            <button @click="continuityStore.resumeWork" class="btn-resume">Resume</button>
            <button @click="continuityStore.showResumePrompt = false" class="btn-dismiss" aria-label="Dismiss"><AppIcon name="x" :size="16" /></button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Floating theme toggle on auth pages (no shell) -->
    <button
      v-if="!showChrome"
      @click="toggleTheme"
      class="theme-toggle fixed top-4 right-4 z-40 p-2 rounded-full transition-all"
      :class="isDarkMode ? 'bg-slate-900 text-amber-300' : 'bg-white text-[#0F6CBD]'"
      title="Toggle dark/light theme"
    >
      <AppIcon :name="isDarkMode ? 'sun' : 'moon'" :size="20" label="Toggle dark/light theme" />
    </button>

    <!-- Top bar: mobile entry point to the drawer for signed-in users,
         and the only chrome logged-out visitors see on preview pages. -->
    <header
      v-if="showChrome"
      class="app-topbar"
      :class="{ 'app-topbar--authed': showSidebar }"
    >
      <div class="topbar-inner">
        <button
          v-if="showSidebar"
          class="drawer-toggle"
          type="button"
          aria-label="Open navigation"
          :aria-expanded="drawerOpen ? 'true' : 'false'"
          @click="drawerOpen = !drawerOpen"
        >
          <AppIcon :name="drawerOpen ? 'x' : 'menu'" :size="24" />
        </button>
        <BrandLogo :to="isAuthed ? '/dashboard' : '/login'" size="sm" text="Apolaki" class="topbar-brand" />
        <div class="topbar-actions">
          <button @click="toggleTheme" class="topbar-theme" type="button" title="Toggle dark/light theme">
            <AppIcon :name="isDarkMode ? 'sun' : 'moon'" :size="20" label="Toggle dark/light theme" />
          </button>
          <template v-if="!isAuthed">
            <router-link to="/login" class="topbar-login">Log in</router-link>
            <router-link to="/signup" class="topbar-signup">Sign up</router-link>
          </template>
        </div>
      </div>
    </header>

    <div class="app-body">
      <!-- Sidebar shell: static column on desktop, slide-in drawer on mobile -->
      <transition name="backdrop-fade">
        <div v-if="showSidebar && drawerOpen" class="drawer-backdrop" @click="closeDrawer"></div>
      </transition>

      <aside
        v-if="showSidebar"
        class="app-sidebar"
        :class="{ 'app-sidebar--open': drawerOpen }"
        aria-label="Main navigation"
      >
        <!-- Profile at the top -->
        <router-link to="/profile" class="sidebar-profile" @click="closeDrawer">
          <span class="sidebar-avatar">{{ avatarInitial }}</span>
          <span class="sidebar-profile__meta">
            <strong>{{ displayName }}</strong>
            <small>{{ roleLabel }} · View profile</small>
          </span>
          <AppIcon name="chevron-right" :size="16" class="sidebar-profile__chevron" />
        </router-link>

        <nav class="sidebar-nav">
          <router-link to="/dashboard" class="sidebar-link" @click="closeDrawer"><AppIcon name="home" :size="20" /> Home</router-link>
          <router-link to="/assessment" class="sidebar-link" @click="closeDrawer"><AppIcon name="pencil-ruler" :size="20" /> Assessment</router-link>
          <router-link to="/finance" class="sidebar-link" @click="closeDrawer"><AppIcon name="wallet" :size="20" /> Financing</router-link>
          <router-link to="/marketplace" class="sidebar-link" @click="closeDrawer"><AppIcon name="store" :size="20" /> Marketplace</router-link>
          <router-link to="/installations" class="sidebar-link" @click="closeDrawer"><AppIcon name="hard-hat" :size="20" /> Installations</router-link>
          <router-link to="/monitoring" class="sidebar-link" @click="closeDrawer"><AppIcon name="activity" :size="20" /> Monitoring</router-link>
          <router-link to="/contracts" class="sidebar-link" @click="closeDrawer"><AppIcon name="file-signature" :size="20" /> Contracts</router-link>

          <span class="sidebar-section"><AppIcon name="wrench" :size="16" /> Tools</span>
          <router-link to="/solar-scout" class="sidebar-link" @click="closeDrawer"><AppIcon name="satellite-dish" :size="20" /> Solar Scout</router-link>
          <router-link to="/installer-feed" class="sidebar-link" @click="closeDrawer"><AppIcon name="camera" :size="20" /> Install Feed</router-link>
          <button type="button" class="sidebar-link sidebar-link--button" @click="openMessages">
            <AppIcon name="message-circle" :size="20" /> Messages <span v-if="unreadCount" class="unread-dot"></span>
          </button>
          <router-link to="/messaging?support=true" class="sidebar-link" @click="closeDrawer"><AppIcon name="life-buoy" :size="20" /> Get Help</router-link>

          <template v-if="hasRoleLinks">
            <span class="sidebar-section"><AppIcon name="briefcase" :size="16" /> Workspace</span>
            <router-link v-if="canOpenQuotes" to="/quotes" class="sidebar-link" @click="closeDrawer"><AppIcon name="receipt" :size="20" /> Quotes</router-link>
            <router-link v-if="canOpenQuotes" to="/my-public-profile" class="sidebar-link" @click="closeDrawer"><AppIcon name="sparkles" :size="20" /> My Public Profile</router-link>
            <router-link v-if="canOpenDealerPortal" to="/dealer" class="sidebar-link" @click="closeDrawer"><AppIcon name="handshake" :size="20" /> Dealer Portal</router-link>
            <router-link v-if="canOpenOperationsPortal" to="/operations" class="sidebar-link" @click="closeDrawer"><AppIcon name="settings" :size="20" /> Operations</router-link>
            <router-link v-if="canOpenAdminPortal" to="/admin" class="sidebar-link" @click="closeDrawer"><AppIcon name="user-cog" :size="20" /> Admin</router-link>
            <router-link v-if="canOpenBreakGlassPortal" to="/superadmin" class="sidebar-link sidebar-link--emergency" @click="closeDrawer"><AppIcon name="shield-alert" :size="20" /> Break-Glass</router-link>
          </template>
        </nav>

        <!-- Logout pinned to the bottom -->
        <div class="sidebar-footer">
          <button @click="toggleTheme" class="sidebar-theme" type="button">
            <AppIcon :name="isDarkMode ? 'sun' : 'moon'" :size="16" /> {{ isDarkMode ? 'Light mode' : 'Dark mode' }}
          </button>
          <button @click="handleLogout" class="sidebar-logout" type="button"><AppIcon name="log-out" :size="16" /> Log out</button>
        </div>
      </aside>

      <main class="main-content">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </main>
    </div>

    <!-- Messaging Widget Overlay -->
    <!-- (Assuming widget exists elsewhere or integrated here) -->
  </div>
</template>

<style>
:root {
  --primary-blue: #0F6CBD;
  --warning-orange: #f59e0b;
  --sidebar-width: 264px;
}

.app-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.continuity-banner {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #f8fafc;
  padding: 0.75rem 1.25rem;
  position: sticky;
  top: 0;
  z-index: 1001;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.btn-resume {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.4rem 1rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-dismiss {
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
}

/* ── Top bar (guest chrome + mobile drawer trigger) ─────────────── */
.app-topbar {
  position: sticky;
  top: 0;
  z-index: 60;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

.dark-theme .app-topbar {
  background: #0f172a;
  border-bottom-color: #1e293b;
}

.topbar-inner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 80rem;
  margin: 0 auto;
  padding: 0.5rem 1rem;
}

.topbar-brand { color: var(--primary-blue); }
.dark-theme .topbar-brand { color: #f1f5f9; }

.topbar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.drawer-toggle {
  background: transparent;
  border: none;
  padding: 0.4rem;
  border-radius: 0.5rem;
  color: #334155;
  cursor: pointer;
}
.drawer-toggle:hover { background: rgba(15, 108, 189, 0.08); }
.dark-theme .drawer-toggle { color: #e2e8f0; }
.dark-theme .drawer-toggle:hover { background: rgba(255, 255, 255, 0.08); }

.topbar-theme {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.35rem;
  border-radius: 999px;
}

.topbar-login {
  color: var(--primary-blue);
  font-weight: 600;
  text-decoration: none;
  padding: 0.45rem 0.9rem;
  border-radius: 0.5rem;
}
.dark-theme .topbar-login { color: #93c5fd; }

.topbar-signup {
  background: var(--primary-blue);
  color: #ffffff;
  font-weight: 600;
  text-decoration: none;
  padding: 0.45rem 0.9rem;
  border-radius: 0.5rem;
}

/* Signed-in users only need the top bar as a drawer trigger on small screens. */
@media (min-width: 1024px) {
  .app-topbar--authed { display: none; }
}

/* ── Sidebar shell ──────────────────────────────────────────────── */
.app-body {
  display: flex;
  flex: 1;
  min-height: 0;
  align-items: stretch;
}

.app-sidebar {
  width: var(--sidebar-width);
  flex: 0 0 var(--sidebar-width);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  z-index: 70;
}

.dark-theme .app-sidebar {
  background: #0f172a;
  border-right-color: #1e293b;
}

.sidebar-profile {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.9rem 1rem;
  text-decoration: none;
  color: #0f172a;
  border-bottom: 1px solid #eef2f7;
}
.sidebar-profile:hover { background: rgba(15, 108, 189, 0.06); }
.dark-theme .sidebar-profile { color: #f1f5f9; border-bottom-color: #1e293b; }
.dark-theme .sidebar-profile:hover { background: rgba(255, 255, 255, 0.05); }

.sidebar-avatar {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  border-radius: 50%;
  background: var(--primary-blue);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
}

.sidebar-profile__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.sidebar-profile__meta strong {
  font-size: 0.9rem;
  font-weight: 650;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar-profile__meta small {
  font-size: 0.72rem;
  color: #64748b;
}
.dark-theme .sidebar-profile__meta small { color: #94a3b8; }

.sidebar-profile__chevron {
  margin-left: auto;
  color: #94a3b8;
}
.dark-theme .sidebar-profile__chevron { color: #64748b; }

.sidebar-nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0.75rem 0.6rem;
  overflow-y: auto;
}

.sidebar-section {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.9rem 0.5rem 0.25rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.dark-theme .sidebar-section { color: #64748b; }

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.65rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #334155;
  text-decoration: none;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  width: 100%;
  font-family: inherit;
}
.sidebar-link:hover { background: rgba(15, 108, 189, 0.08); color: var(--primary-blue); }
.sidebar-link.router-link-active {
  background: rgba(15, 108, 189, 0.12);
  color: var(--primary-blue);
  font-weight: 650;
}
.dark-theme .sidebar-link { color: #cbd5e1; }
.dark-theme .sidebar-link:hover { background: rgba(255, 255, 255, 0.07); color: #f8fafc; }
.dark-theme .sidebar-link.router-link-active { background: rgba(147, 197, 253, 0.15); color: #93c5fd; }

.sidebar-link--emergency { color: #b91c1c; }
.sidebar-link--emergency:hover { background: rgba(185, 28, 28, 0.08); color: #b91c1c; }
.dark-theme .sidebar-link--emergency { color: #fca5a5; }

.sidebar-footer {
  padding: 0.75rem;
  border-top: 1px solid #eef2f7;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.dark-theme .sidebar-footer { border-top-color: #1e293b; }

.sidebar-theme {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 500;
  color: #64748b;
  padding: 0.45rem 0.65rem;
  border-radius: 0.5rem;
  text-align: left;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 0.45rem;
}
.sidebar-theme:hover { background: rgba(15, 108, 189, 0.06); }
.dark-theme .sidebar-theme { color: #94a3b8; }
.dark-theme .sidebar-theme:hover { background: rgba(255, 255, 255, 0.06); }

.sidebar-logout {
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #334155;
  padding: 0.55rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
}
.sidebar-logout:hover { border-color: #fca5a5; color: #b91c1c; background: rgba(185, 28, 28, 0.04); }
.dark-theme .sidebar-logout { border-color: #334155; color: #e2e8f0; }
.dark-theme .sidebar-logout:hover { border-color: #f87171; color: #fca5a5; }

.main-content {
  flex: 1;
  min-width: 0;
}

/* Mobile: the sidebar becomes a slide-in drawer. */
@media (max-width: 1023px) {
  .app-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    height: 100dvh;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
    box-shadow: none;
  }
  .app-sidebar--open {
    transform: translateX(0);
    box-shadow: 0 10px 40px rgba(2, 6, 23, 0.35);
  }
}

.drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.5);
  z-index: 65;
}
@media (min-width: 1024px) {
  .drawer-backdrop { display: none; }
}

.backdrop-fade-enter-active, .backdrop-fade-leave-active { transition: opacity 0.2s ease; }
.backdrop-fade-enter-from, .backdrop-fade-leave-to { opacity: 0; }

.slide-down-enter-active, .slide-down-leave-active { transition: all 0.3s ease; }
.slide-down-enter-from, .slide-down-leave-to { transform: translateY(-100%); opacity: 0; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.unread-dot { width: 8px; height: 8px; background: var(--warning-orange); border-radius: 50%; display: inline-block; }
</style>
